import { useState, useRef, useCallback, useEffect } from 'react';
import type { OrientationType } from '../shared';

export type ScreenMode = 'screen-only' | 'screen-cam';

interface ScreenRecorderState {
  isSharing: boolean;
  isRecording: boolean;
  duration: number;
  videoBlob: Blob | null;
  videoUrl: string | null;
  error: string | null;
  mode: ScreenMode;
  enableMic: boolean;
  enableSystemAudio: boolean;
  recordedOrientation: OrientationType | null;
}

export const useScreenRecorder = () => {
  const [state, setState] = useState<ScreenRecorderState>({
    isSharing: false,
    isRecording: false,
    duration: 0,
    videoBlob: null,
    videoUrl: null,
    error: null,
    mode: 'screen-cam',
    enableMic: true,
    enableSystemAudio: true,
    recordedOrientation: null,
  });

  const screenRef = useRef<HTMLVideoElement | null>(null);
  const camRef = useRef<HTMLVideoElement | null>(null);
  const screenStream = useRef<MediaStream | null>(null);
  const camStream = useRef<MediaStream | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const setMode = useCallback((mode: ScreenMode) => {
    setState(prev => ({ ...prev, mode }));
  }, []);

  const setEnableMic = useCallback((on: boolean) => {
    setState(prev => ({ ...prev, enableMic: on }));
  }, []);

  const setEnableSystemAudio = useCallback((on: boolean) => {
    setState(prev => ({ ...prev, enableSystemAudio: on }));
  }, []);

  /** Start screen share (and optionally camera) */
  const startShare = useCallback(async () => {
    try {
      // Acquire camera/mic BEFORE screen share — iOS Safari fails if
      // getUserMedia is called after getDisplayMedia.
      if (state.mode === 'screen-cam') {
        try {
          const cam = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: 160, height: 214 },
            audio: state.enableMic,
          });
          camStream.current = cam;
          if (camRef.current) {
            camRef.current.srcObject = cam;
            camRef.current.muted = true;
            await camRef.current.play();
          }
        } catch {
          // Camera optional — continue without it
        }
      } else if (state.enableMic) {
        // Screen-only but with mic: get mic audio separately
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          camStream.current = mic;
        } catch {
          // Mic optional
        }
      }

      // Request screen share
      const displayOptions: DisplayMediaStreamOptions = {
        video: true,
        audio: state.enableSystemAudio,
      };
      const screen = await navigator.mediaDevices.getDisplayMedia(displayOptions);
      screenStream.current = screen;

      if (screenRef.current) {
        screenRef.current.srcObject = screen;
        screenRef.current.muted = true;
        await screenRef.current.play();
      }

      // Handle user stopping screen share via browser UI
      screen.getVideoTracks()[0].onended = () => {
        stopShare();
      };

      setState(prev => ({ ...prev, isSharing: true, error: null }));
    } catch (err) {
      // If screen share denied, stop any camera we already started
      if (camStream.current) {
        camStream.current.getTracks().forEach(t => t.stop());
        camStream.current = null;
      }
      setState(prev => ({ ...prev, error: 'Screen share denied' }));
      throw err;
    }
  }, [state.mode, state.enableMic, state.enableSystemAudio]);

  /** Start recording the shared screen + optional cam/mic */
  const startRecording = useCallback((currentOrientation?: OrientationType) => {
    if (!screenStream.current) return;

    // Combine tracks: screen video + mixed audio via AudioContext
    const tracks: MediaStreamTrack[] = [
      ...screenStream.current.getVideoTracks(),
    ];

    const hasSystemAudio = screenStream.current.getAudioTracks().length > 0;
    const hasMicAudio = camStream.current ? camStream.current.getAudioTracks().length > 0 : false;

    if (hasSystemAudio || hasMicAudio) {
      // Use AudioContext to mix multiple audio sources into one track
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const destination = audioCtx.createMediaStreamDestination();

      if (hasSystemAudio) {
        const systemSource = audioCtx.createMediaStreamSource(
          new MediaStream(screenStream.current.getAudioTracks())
        );
        systemSource.connect(destination);
        // Route system audio to speakers so user can hear the app
        systemSource.connect(audioCtx.destination);
      }

      if (hasMicAudio) {
        const micSource = audioCtx.createMediaStreamSource(
          new MediaStream(camStream.current!.getAudioTracks())
        );
        micSource.connect(destination);
      }

      // Add the single mixed audio track
      tracks.push(...destination.stream.getAudioTracks());
    }

    const combinedStream = new MediaStream(tracks);
    chunks.current = [];

    // Detect best MIME type — Safari prefers mp4, Chrome/Firefox prefer webm
    const mimeCandidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
    const mimeType = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m)) || '';
    // 2.5 Mbps at capture — screen content compresses well and an uncapped
    // recorder can emit 8+ Mbps, producing files the upload pipeline then
    // has to shrink anyway (media-compression spec, capture-time control).
    const recorderOptions: MediaRecorderOptions = {
      ...(mimeType ? { mimeType } : {}),
      // Intermediate only — the transcode pass re-encodes to its own target,
      // so give it a clean 4 Mbps source rather than pre-squeezed 2.5.
      videoBitsPerSecond: 4_000_000,
    };
    const recorder = new MediaRecorder(combinedStream, recorderOptions);

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const actualType = recorder.mimeType || mimeType || 'video/webm';
      const blob = new Blob(chunks.current, { type: actualType });
      const url = URL.createObjectURL(blob);
      setState(prev => ({ ...prev, videoBlob: blob, videoUrl: url }));
    };

    recorder.start();
    mediaRecorder.current = recorder;

    setState(prev => ({
      ...prev,
      isRecording: true,
      duration: 0,
      videoBlob: null,
      videoUrl: null,
      recordedOrientation: currentOrientation || null,
    }));

    timerRef.current = setInterval(() => {
      setState(prev => ({ ...prev, duration: prev.duration + 1 }));
    }, 1000);
  }, []);

  /** Stop recording (keeps share alive for preview) */
  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    clearTimer();
    setState(prev => ({ ...prev, isRecording: false }));
  }, [clearTimer]);

  /** Stop all streams */
  const stopShare = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (screenStream.current) {
      screenStream.current.getTracks().forEach(t => t.stop());
      screenStream.current = null;
    }
    if (camStream.current) {
      camStream.current.getTracks().forEach(t => t.stop());
      camStream.current = null;
    }
    if (screenRef.current) screenRef.current.srcObject = null;
    if (camRef.current) camRef.current.srcObject = null;
    clearTimer();
    setState(prev => ({ ...prev, isSharing: false, isRecording: false }));
  }, [clearTimer]);

  /** Inject a pre-recorded video (e.g. from a restored draft) */
  const injectVideo = useCallback((blob: Blob, dur: number, orientation: OrientationType | null) => {
    const url = URL.createObjectURL(blob);
    setState(prev => ({
      ...prev,
      videoBlob: blob,
      videoUrl: url,
      duration: dur,
      recordedOrientation: orientation,
    }));
  }, []);

  /** Reset for retake (keeps streams stopped) */
  const reset = useCallback(() => {
    if (state.videoUrl) {
      URL.revokeObjectURL(state.videoUrl);
    }
    setState(prev => ({
      ...prev,
      duration: 0,
      videoBlob: null,
      videoUrl: null,
      error: null,
    }));
  }, [state.videoUrl]);

  /** Full reset */
  const resetAll = useCallback(() => {
    stopShare();
    if (state.videoUrl) {
      URL.revokeObjectURL(state.videoUrl);
    }
    setState({
      isSharing: false,
      isRecording: false,
      duration: 0,
      videoBlob: null,
      videoUrl: null,
      error: null,
      mode: 'screen-cam',
      enableMic: true,
      enableSystemAudio: true,
      recordedOrientation: null,
    });
  }, [stopShare, state.videoUrl]);

  /* Cleanup on unmount */
  useEffect(() => {
    return () => {
      clearTimer();
      if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
        mediaRecorder.current.stop();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (screenStream.current) {
        screenStream.current.getTracks().forEach(t => t.stop());
      }
      if (camStream.current) {
        camStream.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [clearTimer]);

  return {
    ...state,
    screenRef,
    camRef,
    setMode,
    setEnableMic,
    setEnableSystemAudio,
    startShare,
    startRecording,
    stopRecording,
    injectVideo,
    stopShare,
    reset,
    resetAll,
  };
};
